'use client';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SearchProps } from './types';
import { Button } from '../../button';

import IconDotsLoader from '@/assets/icons/IconDotsLoader.svg';
import IconLinkButton from '@/assets/icons/iconLinkButton.svg';
import IconSearch from '@/assets/icons/iconSearch.svg';
import searchText, { SearchResponseItem } from '@/shared/api/searchText';
import { DEFAULT_LOCALE } from '@/shared/constants/common';
import { classNames } from '@/shared/lib/classNames/classNames';

export const Search = ({
  lang,
  placeholder,
  notFound,
  showFullResults,
}: SearchProps) => {
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<
    SearchResponseItem[] | undefined
  >();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const body = document.querySelector('body');
    if (body) body.style.overflowY = isSearchOpen ? 'hidden' : 'initial';
  }, [isSearchOpen]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchRequest = useCallback(
    debounce((val, lang, limit, offset) => {
      if (val < 3) {
        setSearchResults(undefined);
        setIsLoading(false);

        return;
      }

      searchText({ val, lang, limit, offset }).then((res) => {
        setIsLoading(false);
        if (res) {
          setSearchResults(res.items?.filter(({ pageTitle }) => pageTitle));
        }
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (inputValue.length > 0) {
      setIsLoading(true);
    }

    searchRequest(inputValue, lang, 5, 1);
  }, [inputValue, searchRequest, lang]);

  const handleShowFullResultsClick = () => {
    window.location.href = `${lang === DEFAULT_LOCALE ? '' : `/${lang}`}/search?q=${encodeURIComponent(inputValue)}`;
  };

  return (
    <>
      <div
        className={classNames(
          'h-12 pl-4 transition-[width] duration-500 ease-in-out',
          {
            'w-full': isSearchOpen,
            'w-[200px]': !isSearchOpen,
          },
        )}
      >
        <div
          className={classNames(
            'relative z-20 flex h-full w-full cursor-text items-center gap-1 rounded-xl border-2 border-transparent bg-customSurfaceContrastLow px-2 py-1 hover:border-white/25',
            {
              'border-white/25': isSearchOpen,
            },
          )}
          onClick={() => {
            inputRef.current?.focus();
          }}
        >
          <IconSearch className="h-4 w-4 opacity-[0.36]" />
          <input
            className={classNames(
              'h-full w-full border-none bg-transparent focus:outline-none',
            )}
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            placeholder={placeholder}
            onClick={(e) => e.stopPropagation()}
            onFocus={() => setIsSearchOpen(true)}
          />
          {!!isSearchOpen && (
            <div className="absolute left-0 top-14 z-20 flex max-h-[calc(100vh-10rem)] min-h-44 w-full cursor-default flex-col items-center justify-center gap-3 rounded-xl bg-[#2b2d2e] px-3 text-white">
              {isLoading ? (
                <IconDotsLoader className="h-16 w-16 opacity-50" />
              ) : searchResults?.length!! ? (
                <div className="m-3 flex h-full w-full flex-col gap-3 overflow-y-auto">
                  {searchResults.map((item) => (
                    <div
                      onClick={() => {
                        window.location.href = item.pagePath;
                        window.localStorage.setItem(
                          'searchTargetId',
                          item.name,
                        );
                      }}
                      key={item.id}
                      className="w-full cursor-pointer rounded-xl px-3 py-1 transition-[background-color] hover:bg-customSurfaceContrastLow"
                    >
                      <h3 className="text-lg font-medium">{item.pageTitle}</h3>
                      <p dangerouslySetInnerHTML={{ __html: item.text }} />
                    </div>
                  ))}
                  <div className="w-full">
                    <Button
                      variant="secondary"
                      onClick={handleShowFullResultsClick}
                    >
                      {showFullResults || 'Посмотреть все результаты'}
                      <IconLinkButton />
                    </Button>
                  </div>
                </div>
              ) : (
                <h3>{notFound}</h3>
              )}
            </div>
          )}
        </div>
      </div>
      {isSearchOpen && (
        <div
          onClick={() => setIsSearchOpen(false)}
          className="fixed left-0 top-0 z-10 h-[100vh] w-full bg-[#000000A3]"
        ></div>
      )}
    </>
  );
};
